import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { Alert, Button, Skeleton, Spin, Tag, Tooltip, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
} from '@ant-design/icons';

import { fetchAllPostCandidates } from 'api/aiClipperPost/client';
import { PostAiClipCandidate } from 'api/aiClipperPost/types';
import { getPresignedUrl } from 'api/aiClips/client';
import { AI_POST_CLIPS } from 'constants/routes';
import { formatDuration } from 'helpers/time';
import styles from 'pages/AIClips/AIClips.module.scss';
import { formatScore, getScoreColor } from 'pages/AIClips/clipScore';
import { useClipReviewTheater } from 'pages/AIClips/useClipReviewTheater';
import LazyThumb from './LazyThumb';

const { Text, Paragraph } = Typography;

interface RouteParams {
  sourceMediaPostId: string;
}

/** Convert https://bucket.s3.region.amazonaws.com/key → s3://bucket/key */
function toS3Uri(url: string): string {
  try {
    const u = new URL(url);
    const bucket = u.hostname.split('.')[0];
    const key = u.pathname.replace(/^\//, '');
    return `s3://${bucket}/${key}`;
  } catch {
    return url;
  }
}

const AIPostClipDetail: React.FC = () => {
  const apolloClient = useApolloClient();
  const history = useHistory();
  const { sourceMediaPostId } = useParams<RouteParams>();

  useClipReviewTheater();

  const [candidates, setCandidates] = useState<PostAiClipCandidate[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({});
  const [clipDurations, setClipDurations] = useState<Record<string, number>>(
    {}
  );

  const clipVideoRef = useRef<HTMLVideoElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entities = await fetchAllPostCandidates(apolloClient, {
        sourceMediaPostId,
      });
      setCandidates(entities);

      // Presign all clip URLs in parallel
      const entries = await Promise.all(
        entities.map(async (c) => {
          if (!c.clipUrl) return [c.id, null] as const;
          try {
            const s3Uri = toS3Uri(c.clipUrl);
            const signed = await getPresignedUrl(apolloClient, s3Uri);
            return [c.id, signed] as const;
          } catch {
            return [c.id, c.clipUrl] as const;
          }
        })
      );
      setResolvedUrls(
        Object.fromEntries(entries.filter(([, v]) => v != null)) as Record<
          string,
          string
        >
      );
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load clips.');
    } finally {
      setLoading(false);
    }
  }, [apolloClient, sourceMediaPostId]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = candidates[selectedIdx] ?? null;
  const selectedVideoUrl = selected ? resolvedUrls[selected.id] ?? null : null;

  const handleSelectClip = useCallback(
    (idx: number) => {
      if (idx === selectedIdx) return;
      setVideoLoading(true);
      setSelectedIdx(idx);
    },
    [selectedIdx]
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft' && selectedIdx > 0) {
        e.preventDefault();
        handleSelectClip(selectedIdx - 1);
      } else if (
        e.key === 'ArrowRight' &&
        selectedIdx < candidates.length - 1
      ) {
        e.preventDefault();
        handleSelectClip(selectedIdx + 1);
      } else if (e.key === ' ' && clipVideoRef.current) {
        e.preventDefault();
        if (clipVideoRef.current.paused) clipVideoRef.current.play();
        else clipVideoRef.current.pause();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIdx, candidates.length, handleSelectClip]);

  if (loading) {
    return (
      <div className={styles.container}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push(AI_POST_CLIPS)}
          style={{ marginBottom: 16 }}
        >
          Back to list
        </Button>
        <Alert type="error" message={error} showIcon />
      </div>
    );
  }

  const source = candidates[0]?.sourceMediaPost;

  return (
    <div className={styles.detailPage}>
      {/* ── Top bar ── */}
      <div className={styles.detailHeader}>
        <Tooltip title="Back to post clips">
          <button
            className={styles.backBtn}
            onClick={() => history.push(AI_POST_CLIPS)}
            aria-label="Back"
          >
            <ArrowLeftOutlined />
          </button>
        </Tooltip>
        <Text className={styles.headerTitle}>
          {selected?.topic
            ? `${selected.topic}: ${
                selected.title || `Clip ${selectedIdx + 1}`
              }`
            : source?.title || `Clip ${selectedIdx + 1}`}
        </Text>
        <div className={styles.headerTags}>
          <Tag color="blue">Post</Tag>
          <Tag color="default">{candidates.length} clips</Tag>
          {selected?.score != null && (
            <Tag color={getScoreColor(selected.score)}>
              Score: {formatScore(selected.score)}
            </Tag>
          )}
          {selected?.postCandidateStatus === 'Accepted' && (
            <Tag color="green">Accepted</Tag>
          )}
          {selected?.postCandidateStatus === 'Rejected' && (
            <Tag color="red">Rejected</Tag>
          )}
          {selected?.postCandidateStatus === 'NotReviewed' && (
            <Tag color="orange">Pending</Tag>
          )}
        </div>
      </div>

      <div className={styles.mainLayout}>
        {/* Left: sidebar */}
        <div className={styles.clipSidebar}>
          <div className={styles.clipSidebarHeader}>
            <Text strong style={{ fontSize: 13 }}>
              Clips
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {candidates.length} total
            </Text>
          </div>
          <div className={styles.clipSidebarList}>
            {candidates.map((c, idx) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                className={`${styles.clipCard} ${
                  idx === selectedIdx ? styles.clipCardSelected : ''
                }`}
                onClick={() => handleSelectClip(idx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectClip(idx);
                  }
                }}
              >
                <LazyThumb
                  thumbUrl={c.thumbnailUrl}
                  clipUrl={resolvedUrls[c.id] ?? c.clipUrl}
                  idx={idx}
                  duration={clipDurations[c.id]}
                />
                <div className={styles.clipCardFooter}>
                  <div className={styles.clipCardMeta}>
                    <Text strong style={{ fontSize: 11 }}>
                      Clip {idx + 1}
                    </Text>
                  </div>
                  <Tooltip title="Download">
                    <Button
                      size="small"
                      type="text"
                      icon={<DownloadOutlined style={{ fontSize: 12 }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = resolvedUrls[c.id] ?? c.clipUrl;
                        if (url)
                          window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                    />
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Center: video */}
        <div className={styles.centerColumn}>
          <div className={styles.clipViewer}>
            <div className={styles.clipViewerBody}>
              {selectedIdx > 0 && (
                <button
                  className={styles.navPrev}
                  onClick={() => handleSelectClip(selectedIdx - 1)}
                  aria-label="Previous"
                >
                  <LeftOutlined />
                </button>
              )}
              {selectedIdx < candidates.length - 1 && (
                <button
                  className={styles.navNext}
                  onClick={() => handleSelectClip(selectedIdx + 1)}
                  aria-label="Next"
                >
                  <RightOutlined />
                </button>
              )}
              {videoLoading && selectedVideoUrl && (
                <div className={styles.videoLoadingOverlay}>
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 32 }} spin />
                    }
                  />
                </div>
              )}
              {selectedVideoUrl ? (
                <video
                  key={selectedVideoUrl}
                  ref={clipVideoRef}
                  src={selectedVideoUrl}
                  poster={selected?.thumbnailUrl ?? undefined}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className={`${styles.videoPlayerPortrait} ${
                    videoLoading ? styles.videoFadeIn : ''
                  }`}
                  onLoadedMetadata={(e) => {
                    if (!selected) return;
                    const d = e.currentTarget.duration;
                    if (d && isFinite(d))
                      setClipDurations((p) => ({ ...p, [selected.id]: d }));
                  }}
                  onCanPlay={() => setVideoLoading(false)}
                  onWaiting={() => setVideoLoading(true)}
                  onPlaying={() => setVideoLoading(false)}
                >
                  <track kind="captions" src="" srcLang="en" label="English" />
                </video>
              ) : (
                <div className={styles.noVideo}>
                  <Text type="secondary">
                    {loading ? 'Loading…' : 'No video available'}
                  </Text>
                </div>
              )}
            </div>
            <div className={styles.clipInfoBar}>
              <span>
                Clip {selectedIdx + 1} / {candidates.length}
              </span>
              {clipDurations[selected?.id ?? ''] != null && (
                <span className={styles.clipDuration}>
                  {formatDuration(clipDurations[selected?.id ?? ''] ?? 0)}
                </span>
              )}
              <span className={styles.clipKeyHint}>
                Use arrow keys to navigate
              </span>
            </div>
          </div>
        </div>

        {/* Right: read-only review info */}
        <div className={styles.rightWorkspace}>
          <div className={styles.chatPanel}>
            <div className={styles.chatHeader}>
              <Text strong style={{ fontSize: 13 }}>
                Review
              </Text>
              {selected && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Clip {selectedIdx + 1}
                </Text>
              )}
            </div>
            <div className={styles.chatMessages}>
              {selected ? (
                <>
                  {selected.topic && (
                    <div className={styles.chatBubble}>
                      <div className={styles.chatBubbleHeader}>Topic</div>
                      <Text style={{ fontSize: 13 }}>{selected.topic}</Text>
                    </div>
                  )}
                  {selected.title && (
                    <div className={styles.chatBubble}>
                      <div className={styles.chatBubbleHeader}>Title</div>
                      <Text style={{ fontSize: 13 }}>{selected.title}</Text>
                    </div>
                  )}
                  {selected.description && (
                    <div className={styles.chatBubble}>
                      <div className={styles.chatBubbleHeader}>Body</div>
                      <Paragraph
                        style={{ fontSize: 12, marginBottom: 0 }}
                        ellipsis={{ rows: 5, expandable: true, symbol: 'more' }}
                      >
                        {selected.description}
                      </Paragraph>
                    </div>
                  )}
                  {selected.transcriptExcerpt && (
                    <div className={styles.chatBubble}>
                      <div className={styles.chatBubbleHeader}>
                        Transcript excerpt
                      </div>
                      <Paragraph
                        style={{ fontSize: 12, marginBottom: 0 }}
                        ellipsis={{ rows: 4, expandable: true, symbol: 'more' }}
                      >
                        {selected.transcriptExcerpt}
                      </Paragraph>
                    </div>
                  )}
                </>
              ) : (
                <Text type="secondary" className={styles.emptyNotes}>
                  Select a clip to review
                </Text>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPostClipDetail;
